import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Clock, Users, BookOpen, GraduationCap, Star, ChevronRight, Globe, Brain, Beaker, Award, Sparkles } from 'lucide-react';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import SEOHead from './SEOHead';
import { usePlanPrices } from '../utils/livePrices';
import axios from 'axios';
import { getCachedCourses, setCachedCourses } from './Dashboard';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Utility function to strip HTML tags from text
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Category configuration with JNTU plan mapping and benefits
const categoryConfig = {
  'engineering-it': {
    name: 'Engineering & IT Success Plan',
    jntuPlanName: 'Engineering Student Success Plan',
    subtitle: '(For Scholarship Holders)',
    dbCategory: 'IT & Engineering',
    description: 'Master technology and engineering skills with industry-leading courses',
    color: 'from-blue-600 to-indigo-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    icon: '💻',
    planId: 'engineering-student-success',
    price: 3540,
    duration: '12 Months',
    benefits: [
      { title: 'GRE Exam Prep', description: 'Complete GRE question bank with detailed solutions', icon: Globe, iconBg: 'bg-green-500', textColor: 'text-green-600', link: '/global-exam-bank' },
      { title: 'GMAT Exam Prep', description: 'Master GMAT with comprehensive practice questions', icon: Globe, iconBg: 'bg-purple-500', textColor: 'text-purple-600', link: '/global-exam-bank' },
      { title: 'Research AI', description: 'Learn Research with Nerd-AI and understand published papers', icon: Beaker, iconBg: 'bg-blue-500', textColor: 'text-blue-600', link: '/learn-research' },
      { title: 'Research Proposal', description: 'Create professional Research Proposal Templates', icon: BookOpen, iconBg: 'bg-indigo-500', textColor: 'text-indigo-600', link: '/learn-research' },
      { title: 'AI Buddy', description: '1500 Free Nerd AI Credits for doubt solving (90 Days Validity)', icon: Brain, iconBg: 'bg-orange-500', textColor: 'text-orange-600', link: '/ai-advisor' },
      { title: 'MS Abroad Scholarship Search', description: 'Get free Masters admission abroad with Scholarship Search', icon: GraduationCap, iconBg: 'bg-cyan-500', textColor: 'text-cyan-600', link: null }
    ]
  },
  'finance-management': {
    name: 'Finance & Management Success Plan',
    jntuPlanName: 'Business & Finance Student Success Plan',
    subtitle: '(For Scholarship Holders)',
    dbCategory: 'Finance & Management',
    description: 'Build expertise in finance, business, and management domains',
    color: 'from-emerald-600 to-teal-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    icon: '📊',
    planId: 'business-finance-student-success',
    price: 3540,
    duration: '12 Months',
    benefits: [
      { title: 'GRE Exam Prep', description: 'Complete GRE question bank with detailed solutions', icon: Globe, iconBg: 'bg-green-500', textColor: 'text-green-600', link: '/global-exam-bank' },
      { title: 'GMAT Exam Prep', description: 'Master GMAT with comprehensive practice questions', icon: Globe, iconBg: 'bg-purple-500', textColor: 'text-purple-600', link: '/global-exam-bank' },
      { title: 'Research AI', description: 'Learn Research with Nerd-AI and understand published papers', icon: Beaker, iconBg: 'bg-blue-500', textColor: 'text-blue-600', link: '/learn-research' },
      { title: 'Research Proposal', description: 'Create professional Research Proposal Templates', icon: BookOpen, iconBg: 'bg-indigo-500', textColor: 'text-indigo-600', link: '/learn-research' },
      { title: 'AI Buddy', description: '1500 Free Nerd AI Credits for doubt solving (90 Days Validity)', icon: Brain, iconBg: 'bg-orange-500', textColor: 'text-orange-600', link: '/ai-advisor' },
      { title: 'MS Abroad Scholarship Search', description: 'Get free Masters admission abroad with Scholarship Search', icon: GraduationCap, iconBg: 'bg-cyan-500', textColor: 'text-cyan-600', link: null }
    ]
  },
  'pharma-lifesciences': {
    name: 'Pharma & Life-Sciences Success Plan',
    jntuPlanName: 'Pharma & Life-Sciences Success Plan',
    subtitle: '(For Scholarship Holders)',
    dbCategory: 'Pharma & Life-Sciences',
    description: 'Advance your career in pharmaceutical and life sciences industry',
    color: 'from-purple-600 to-pink-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    icon: '🔬',
    planId: 'pharma-student-success',
    price: 3540,
    duration: '12 Months',
    benefits: [
      { title: 'GRE Exam Prep', description: 'Complete GRE question bank with detailed solutions', icon: Globe, iconBg: 'bg-green-500', textColor: 'text-green-600', link: '/global-exam-bank' },
      { title: 'GMAT Exam Prep', description: 'Master GMAT with comprehensive practice questions', icon: Globe, iconBg: 'bg-purple-500', textColor: 'text-purple-600', link: '/global-exam-bank' },
      { title: 'International Pharmacist Bank', description: 'NAPLEX, MPJE, PEBC, GPhC, OPRA, CAP, DHA, SPLE', icon: Award, iconBg: 'bg-teal-500', textColor: 'text-teal-600', link: '/pharmacy-exam-bank' },
      { title: 'Research AI', description: 'Learn Research with Nerd-AI and understand published papers', icon: Beaker, iconBg: 'bg-blue-500', textColor: 'text-blue-600', link: '/learn-research' },
      { title: 'Research Proposal', description: 'Create professional Research Proposal Templates', icon: BookOpen, iconBg: 'bg-indigo-500', textColor: 'text-indigo-600', link: '/learn-research' },
      { title: 'AI Buddy', description: '1500 Free Nerd AI Credits for doubt solving (90 Days Validity)', icon: Brain, iconBg: 'bg-orange-500', textColor: 'text-orange-600', link: '/ai-advisor' },
      { title: 'MS Abroad Scholarship Search', description: 'Get free Masters admission abroad with Scholarship Search', icon: GraduationCap, iconBg: 'bg-cyan-500', textColor: 'text-cyan-600', link: null }
    ]
  },
  'healthcare': {
    name: 'Healthcare Success Plan',
    dbCategory: 'Medicine',
    description: 'Excel in medical and healthcare professional development',
    color: 'from-red-600 to-orange-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    icon: '🏥'
  }
};

const CategoryCourses = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const category = categoryConfig[categorySlug];
  const livePrices = usePlanPrices();
  // Override display price with backend total (base + GST) if available
  const displayPrice = React.useMemo(() => {
    if (!category) return 0;
    const live = livePrices[category.planId];
    return live?.total_inr ?? category.price;
  }, [category, livePrices]);
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);
  
  useEffect(() => {
    if (category) {
      fetchCourses();
    }
  }, [categorySlug]);
  
  const fetchCourses = async () => {
    try {
      // First, try to use cached courses for instant display
      const cachedCourses = getCachedCourses();
      
      if (cachedCourses) {
        // Use cached data - INSTANT display
        const filteredCourses = cachedCourses.filter(course => {
          const courseCategories = course.categories || [];
          return courseCategories.some(cat => 
            cat.toLowerCase() === category.dbCategory.toLowerCase()
          );
        });
        setCourses(filteredCourses);
        setLoading(false);
        return; // Use cache, don't fetch again
      }
      
      // No cache available - fetch from API
      setLoading(true);
      const response = await axios.get(`${API}/api/lms/courses?limit=100`);
      if (response.data.success) {
        const allCourses = response.data.data.courses || [];
        // Cache the courses for future use
        setCachedCourses(allCourses);
        // Filter courses by category
        const filteredCourses = allCourses.filter(course => {
          const courseCategories = course.categories || [];
          return courseCategories.some(cat => 
            cat.toLowerCase() === category.dbCategory.toLowerCase()
          );
        });
        setCourses(filteredCourses);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h1>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${category.title} Courses`}
        description={(category.description || `Browse ${category.title} courses on Nerd Nation. Curated learning paths to advance your career.`).slice(0, 160)}
        path={`/category/${categorySlug}`}
      />
      {/* Hero Section - Mobile Optimized */}
      <div className={`bg-gradient-to-r ${category.color} text-white py-8 sm:py-12 px-4`}>
        <div className="max-w-6xl mx-auto">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 sm:mb-6 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            Back to Home
          </button>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4">
            <span className="text-4xl sm:text-5xl">{category.icon}</span>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">{category.jntuPlanName || category.name}</h1>
              {category.subtitle && (
                <p className="text-white/90 text-sm sm:text-lg mt-1">{category.subtitle}</p>
              )}
              <p className="text-white/80 text-sm sm:text-base mt-2">{category.description}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-4 sm:mt-6 text-white/90 text-sm sm:text-base">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{courses.length} Courses{category.benefits ? ' Included' : ''}</span>
            </div>
            {category.duration && (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{category.duration} Access</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Internationally Recognized</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Experts from Top MNCs</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>1500 AI Credits</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>6 Additional Benefits</span>
            </div>
          </div>
          
          {/* CTA Button - Only show for JNTU plans */}
          {category.planId && category.price && (
            <div className="mt-6 sm:mt-8">
              <Button 
                onClick={() => {
                  if (isMobileApp()) {
                    openWebappWithAuth(`/checkout/${category.planId}`);
                  } else {
                    navigate(`/checkout/${category.planId}`);
                  }
                }}
                className="bg-white text-gray-900 hover:bg-gray-100 px-6 sm:px-8 py-2.5 sm:py-3 text-base sm:text-lg font-semibold rounded-full shadow-lg w-full sm:w-auto"
              >
                {isMobileApp() ? 'Get This Plan' : `Get This Plan - ₹${displayPrice.toLocaleString()}`}
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Scholarship Winners Announcement Section - Mobile Optimized */}
      {category.benefits && category.benefits.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12 md:py-16">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-4 sm:mb-6">
              Recent Orientations
            </h3>
          </div>
          
          {/* YouTube Videos Row - Mobile Optimized */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {/* Video 1 */}
            <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/jCG6wrzYrds"
                  title="Recent Orientation Video 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            {/* Video 2 */}
            <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/ICnVV4Et0vA"
                  title="Recent Orientation Video 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Learning Pathway Section - Only for Pharma & Life-Sciences - Mobile Optimized */}
      {categorySlug === 'pharma-lifesciences' && (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Learning Pathway
            </h2>
          </div>
          
          {/* Learning Pathway Image */}
          <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-4 sm:p-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_b308f987-e406-4337-9df7-87faa40805f4/artifacts/pr569rge_1-Year-Job-Pass-Unlimited-Learning-for-Pharma-Biotech-Biosciences-Students.jpg"
              alt="1 Year JOB PASS Learning Pathway for Pharma & Biotech"
              className="w-full h-auto rounded-lg" loading="lazy" decoding="async"/>
          </div>
        </div>
      )}
      
      {/* Learning Pathway Section - Only for Finance & Management - Mobile Optimized */}
      {categorySlug === 'finance-management' && (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Learning Pathway
            </h2>
          </div>
          
          {/* Learning Pathway Image */}
          <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-4 sm:p-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_b308f987-e406-4337-9df7-87faa40805f4/artifacts/6i96fuv1_1-Year-Job-Pass-Unlimited-Learning-for-BBA-BCom-MBA-Students.jpg"
              alt="1 Year JOB PASS Learning Pathway for BCom, BBA, MBA"
              className="w-full h-auto rounded-lg" loading="lazy" decoding="async"/>
          </div>
        </div>
      )}
      
      {/* Learning Pathway Section - Only for Engineering & IT - Mobile Optimized */}
      {categorySlug === 'engineering-it' && (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Learning Pathway
            </h2>
          </div>
          
          {/* Learning Pathway Image */}
          <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-4 sm:p-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_b308f987-e406-4337-9df7-87faa40805f4/artifacts/49ez4xz5_1-Year-Job-Pass-Unlimited-Learning-for-BTech-BCA-Students.jpg"
              alt="1 Year JOB PASS Learning Pathway for BTech, BCA"
              className="w-full h-auto rounded-lg" loading="lazy" decoding="async"/>
          </div>
        </div>
      )}
      
      {/* Advanced Benefits Section - Mobile Optimized */}
      {category.benefits && category.benefits.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              <Sparkles className="inline-block h-5 w-5 sm:h-7 sm:w-7 text-yellow-500 mr-1.5 sm:mr-2" />
              Advanced Benefits
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">Everything included in your {category.jntuPlanName || category.name}</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {category.benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const cardContent = (
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className={`${benefit.iconBg} p-2.5 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0`}>
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold text-base sm:text-lg ${benefit.textColor} mb-0.5 sm:mb-1`}>{benefit.title}</h3>
                        {benefit.link && <ChevronRight className={`h-4 w-4 ${benefit.textColor}`} />}
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              );
              
              return (
                <Card 
                  key={index}
                  className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1 ${benefit.link ? 'cursor-pointer' : ''}`}
                  onClick={() => benefit.link && navigate(benefit.link)}
                >
                  {cardContent}
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Courses Section - Mobile Optimized */}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {category.benefits ? (
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              <Star className="inline-block h-5 w-5 sm:h-7 sm:w-7 text-yellow-500 mr-1.5 sm:mr-2" />
              Unlimited Access to All Top Courses
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">Full 1-year access to all courses in this category</p>
          </div>
        ) : (
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Available Courses
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">Browse and enroll in courses in this category</p>
          </div>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Courses Available</h2>
            <p className="text-gray-500 mb-6 text-sm sm:text-base">Check back soon for new courses in this category</p>
            <Button onClick={() => navigate('/courses')}>Browse All Courses</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {courses.map((course) => (
              <Card 
                key={course.id}
                className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
                onClick={() => navigate(`/courses/${course.slug || course.id}`)}
              >
                {/* Course Image */}
                <div className="relative h-40 sm:h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                  {(course.thumbnail_url || course.thumbnail) ? (
                    <img 
                      src={course.thumbnail_url || course.thumbnail} 
                      alt={course.title}
                      className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                      <GraduationCap className="h-12 w-12 sm:h-16 sm:w-16 text-white/50" />
                    </div>
                  )}
                  
                  {/* Badge - INCLUDED for JNTU plans, Price for others */}
                  {category.benefits ? (
                    <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-md">
                      ✓ INCLUDED
                    </div>
                  ) : (
                    <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white/95 backdrop-blur-sm px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-md">
                      <span className={`font-bold text-xs sm:text-sm ${course.price_inr ? 'text-gray-900' : 'text-green-600'}`}>
                        {course.price_inr ? `₹${course.price_inr.toLocaleString()}` : 'Free'}
                      </span>
                    </div>
                  )}
                  
                  {/* Mode Badge */}
                  {course.mode && (
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-purple-600 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      {course.mode === 'live' ? 'Live Web' : course.mode === 'self_paced' ? 'Self-Paced' : course.mode}
                    </div>
                  )}
                </div>
                
                {/* Course Info */}
                <CardContent className="p-3 sm:p-4">
                  <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-1.5 sm:mb-2 line-clamp-2">{course.title}</h3>
                  
                  {course.short_description && (
                    <p className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">{stripHtmlTags(course.short_description)}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
                    <div className="flex items-center gap-2 sm:gap-4">
                      {course.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {course.duration}
                        </span>
                      )}
                      {course.total_enrolled > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {course.total_enrolled}
                        </span>
                      )}
                    </div>
                    
                    <ChevronRight className={`h-4 w-4 sm:h-5 sm:w-5 ${category.textColor}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Bottom CTA - Only show for JNTU plans - Mobile Optimized */}
        {courses.length > 0 && category.benefits && category.planId && (
          <div className="text-center mt-6 sm:mt-10 p-5 sm:p-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl sm:rounded-2xl">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2">Get Access to All {courses.length} Courses</h3>
            {!isMobileApp() && (
              <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4">Plus all the Advanced Benefits for just ₹{displayPrice.toLocaleString()}</p>
            )}
            <Button 
              onClick={() => {
                if (isMobileApp()) {
                  openWebappWithAuth(`/checkout/${category.planId}`);
                } else {
                  navigate(`/checkout/${category.planId}`);
                }
              }}
              className={`bg-gradient-to-r ${category.color} text-white px-6 sm:px-8 py-2.5 sm:py-3 text-base sm:text-lg font-semibold rounded-full shadow-lg hover:shadow-xl w-full sm:w-auto ${isMobileApp() ? 'mt-3 sm:mt-4' : ''}`}
            >
              {isMobileApp() ? 'Get This Plan' : 'Get This Plan Now'}
            </Button>
          </div>
        )}
      </div>
      
      {/* Scholarship Winner Videos Section - Auto-scrolling Carousel */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12 md:py-16">
        <div className="text-center mb-6 sm:mb-8 md:mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            🏆 Scholarship Winner Videos
          </h2>
        </div>
        
        {/* Video Carousel Container */}
        <ScholarshipVideosCarousel />
      </div>
      
      <AdvisorRibbon />
      <Footer />
    </div>
  );
};

// Scholarship Videos Carousel Component
const ScholarshipVideosCarousel = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const desktopScrollRef = React.useRef(null);
  const mobileScrollRef = React.useRef(null);
  
  const videos = [
    "qGbT21931fE",
    "pJVl4yTQrlQ",
    "ni7-pxJ7Xwc",
    "1_IqFQwCexg"
  ];
  
  // Auto-scroll effect - scrolls both desktop and mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % videos.length;
        
        // Auto-scroll desktop carousel
        if (desktopScrollRef.current) {
          const cardWidth = 320; // Desktop card width
          const gap = 24; // gap-6 = 24px
          const scrollPosition = (cardWidth + gap) * nextIndex;
          desktopScrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
        
        // Auto-scroll mobile carousel
        if (mobileScrollRef.current) {
          const cardWidth = mobileScrollRef.current.firstChild?.firstChild?.offsetWidth || 0;
          const gap = 16; // gap-4 = 16px
          const scrollPosition = (cardWidth + gap) * nextIndex;
          mobileScrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
        
        return nextIndex;
      });
    }, 4000); // Auto-scroll every 4 seconds
    
    return () => clearInterval(interval);
  }, [videos.length]);
  
  return (
    <div className="relative">
      {/* Desktop View - Horizontal scrollable carousel with auto-scroll */}
      <div className="hidden md:block">
        <div 
          ref={desktopScrollRef}
          className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-purple-400 scrollbar-track-gray-200 hover:scrollbar-thumb-purple-600"
        >
          <div className="flex gap-6 pb-4" style={{ width: 'max-content' }}>
            {videos.map((videoId, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100" style={{ width: '320px', flexShrink: 0 }}>
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={`Scholarship Winner Video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Mobile View - Touch scrollable carousel with auto-scroll */}
      <div className="md:hidden">
        <div 
          ref={mobileScrollRef}
          className="overflow-x-auto overflow-y-hidden -mx-4 px-4"
        >
          <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
            {videos.map((videoId, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100" style={{ width: '85vw', maxWidth: '400px', flexShrink: 0 }}>
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={`Scholarship Winner Video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Carousel Indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                if (mobileScrollRef.current) {
                  const cardWidth = mobileScrollRef.current.firstChild?.firstChild?.offsetWidth || 0;
                  const gap = 16;
                  const scrollPosition = (cardWidth + gap) * index;
                  mobileScrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
                }
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-purple-600 w-6' 
                  : 'bg-gray-300'
              }`}
              aria-label={`Go to video ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryCourses;
